import { useTranslation } from 'react-i18next';
import { usePatronSelector } from '../../../../common/use-patron-selector';
import { contentSelectors } from '../../../../models/content';
import { trackContentCardCopyLink } from '../../../../models/content/analytics';
import {
  Button,
  Icon,
  IconButton,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '@socialchorus/shared-ui-components';
import { usePushToastOrFlashMessage } from '../../toaster/deprecation-helper';
import { uiOperations } from '../../../../models/ui';
import { ID as CopyLinkModalID } from '../copy-link-modal/copy-link-modal';
import { useDispatch } from 'react-redux';

type ContentActionCopyLinkProps = {
  contentId: number;
  analyticsData: IAnalyticsData;
  wideMode?: boolean;
  container?: HTMLElement | null;
};

export function ContentActionCopyLink({
  contentId,
  analyticsData,
  wideMode,
  container,
}: ContentActionCopyLinkProps) {
  const dispatch = useDispatch();
  const { pushMessage } = usePushToastOrFlashMessage();
  const { t } = useTranslation();

  const permalinkUrl = usePatronSelector((state) =>
    contentSelectors.getContentPermalinkUrl(state, { contentId })
  );

  const openCopyLinkModal = (contentURL: any) =>
    dispatch(
      uiOperations.displayOverlay(CopyLinkModalID, {
        contentURL,
      })
    );

  const onClick = () => {
    trackContentCardCopyLink(contentId, analyticsData);

    navigator.clipboard
      .writeText(permalinkUrl)
      .then(() => {
        pushMessage({
          text: t('content.copy_link_success'),
          type: 'success',
        });
      })
      .catch((e) => {
        openCopyLinkModal(permalinkUrl);
      });
  };

  const label = t('content.copy_link');

  if (wideMode) {
    return (
      <Button
        onClick={onClick}
        aria-label={label}
        variant="text"
        leftIcon={<Icon>content_copy</Icon>}
        label={label}
      />
    );
  }

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <IconButton iconName="link" aria-label={label} onClick={onClick} />
      </TooltipTrigger>
      <TooltipContent description={label} container={container} />
    </TooltipRoot>
  );
}
